import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { TrendingUp, TrendingDown, Clock, Wrench, Tractor as TractorIcon, FileCode } from "lucide-react";
import { downloadFile } from "../lib/download";

const COLORS = ["#1B4D3E", "#D97706", "#EA580C", "#0F172A", "#16A34A"];

function KPI({ icon: Icon, label, value, hint, tone = "pine" }) {
  const tones = { pine: "bg-[#1B4D3E]", ochre: "bg-[#D97706]", slate: "bg-slate-900", danger: "bg-rose-600", emerald: "bg-emerald-600" };
  return (
    <Card className="border-slate-200 tp-fade">
      <CardContent className="p-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
          <div className="mt-1 text-2xl font-extrabold text-slate-900 truncate">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-xl text-white grid place-items-center ${tones[tone]}`}><Icon size={20} /></div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const [pnl, setPnl] = useState(null);
  const [trend, setTrend] = useState(null);
  const [servicePeriod, setServicePeriod] = useState("monthly");

  useEffect(() => {
    api.get("/reports/pnl").then((r) => setPnl(r.data));
    api.get("/reports/revenue-trend").then((r) => setTrend(r.data));
  }, []);

  if (!pnl) return <div className="text-slate-500">Yükleniyor…</div>;

  const ts = pnl.tractor_sales;
  const sv = pnl.service;
  const series = sv[servicePeriod] || [];

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Analiz</div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Kâr / Zarar Raporları</h1>
      </div>

      {/* Yearly trend & branch breakdown */}
      {trend && (
        <Card className="border-slate-200">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Ciro Trendi</div>
                <div className="text-lg font-bold text-slate-900">Aylık Ciro (Son 12 Ay)</div>
              </div>
              {trend.best_month && (
                <Badge variant="outline" className="text-xs">
                  En iyi ay: <span className="font-mono ml-1">{trend.best_month.period}</span> · <span className="font-bold text-[#1B4D3E] ml-1">{fmtTRY(trend.best_month.gelir)}</span>
                </Badge>
              )}
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend.monthly} stackOffset="sign">
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                  <Tooltip formatter={(v) => fmtTRY(v)} labelFormatter={(l) => `Ay: ${l}`} />
                  <Legend />
                  <Bar dataKey="satis" stackId="a" fill="#1B4D3E" name="Satış (traktör)" />
                  <Bar dataKey="servis" stackId="a" fill="#D97706" name="Servis" />
                  <Bar dataKey="genel" stackId="a" fill="#0F172A" name="Diğer" />
                  <Line dataKey="gelir" stroke="#EA580C" strokeWidth={2.5} name="Toplam Ciro" type="monotone" dot={{ r: 3 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <Card className="border-slate-200"><CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Cari Yıl</div>
                {trend.yearly.slice(-1).map((y) => (
                  <div key={y.period}>
                    <div className="text-2xl font-extrabold text-slate-900">{y.period}</div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div><div className="text-[10px] text-slate-500">Gelir</div><div className="text-sm font-bold text-emerald-700">{fmtTRY(y.gelir)}</div></div>
                      <div><div className="text-[10px] text-slate-500">Gider</div><div className="text-sm font-bold text-rose-600">{fmtTRY(y.gider)}</div></div>
                      <div><div className="text-[10px] text-slate-500">Net</div><div className={`text-sm font-extrabold ${y.net >= 0 ? "text-[#1B4D3E]" : "text-rose-600"}`}>{fmtTRY(y.net)}</div></div>
                    </div>
                  </div>
                ))}
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Son 5 Yıl</div>
                <div className="space-y-1.5">
                  {trend.yearly.map((y) => (
                    <div key={y.period} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-slate-700">{y.period}</span>
                      <span className={`font-bold ${y.net >= 0 ? "text-[#1B4D3E]" : "text-rose-600"}`}>{fmtTRY(y.net)}</span>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Gelir Kategorileri (Top)</div>
                <div className="space-y-1.5">
                  {trend.top_categories.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-slate-700 truncate">{c.category}</span>
                      <span className="font-bold text-[#1B4D3E]">{fmtTRY(c.amount)}</span>
                    </div>
                  ))}
                  {trend.top_categories.length === 0 && <div className="text-xs text-slate-400">Veri yok.</div>}
                </div>
              </CardContent></Card>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tractor Sales P&L */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><TractorIcon size={16} className="text-[#1B4D3E]" /> Traktör Satış Kâr/Zarar</div>
          <Badge variant="outline">{ts.count} adet satış</Badge>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <KPI icon={TrendingUp} label="Toplam Ciro" value={fmtTRY(ts.revenue)} tone="emerald" />
          <KPI icon={TrendingDown} label="Toplam Maliyet" value={fmtTRY(ts.cost)} tone="slate" hint="Alış fiyatları toplamı" />
          <KPI icon={TrendingUp} label="Kâr / Zarar" value={fmtTRY(ts.profit)} tone={ts.profit >= 0 ? "pine" : "danger"} />
        </div>

        <Card className="border-slate-200 mt-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Traktör</TableHead>
                  <TableHead>Alıcı</TableHead>
                  <TableHead className="text-right">Alış</TableHead>
                  <TableHead className="text-right">Satış</TableHead>
                  <TableHead className="text-right">Takas</TableHead>
                  <TableHead className="text-right">Kâr</TableHead>
                  <TableHead>Fatura</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ts.items.map((s) => (
                  <TableRow key={s.id} data-testid={`pnl-sale-${s.id}`}>
                    <TableCell className="text-xs">{fmtDate(s.date)}</TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{s.tractor_label}</div>
                      <div className="text-xs text-slate-500">{s.condition === "sifir" ? "SIFIR" : "2. EL"}</div>
                    </TableCell>
                    <TableCell>{s.buyer_name}</TableCell>
                    <TableCell className="text-right">{fmtTRY(s.purchase_price)}</TableCell>
                    <TableCell className="text-right font-bold">{fmtTRY(s.sale_price)}</TableCell>
                    <TableCell className="text-right">{s.trade_in_value ? fmtTRY(s.trade_in_value) : "—"}</TableCell>
                    <TableCell className={`text-right font-extrabold ${s.profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{fmtTRY(s.profit)}</TableCell>
                    <TableCell>
                      <button className="text-xs text-[#1B4D3E] font-semibold hover:underline inline-flex items-center gap-1" onClick={() => downloadFile(`/invoices/tractor-sale/${s.id}.xml`, `fatura-${s.id.slice(0,8)}.xml`)} data-testid={`invoice-${s.id}`}>
                        <FileCode size={12} /> XML
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
                {ts.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Henüz satış yok.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>

      {/* Service P&L */}
      <div className="pt-4 border-t border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-bold text-slate-800 flex items-center gap-2"><Wrench size={16} className="text-[#D97706]" /> Servis Kârlılığı</div>
          <Tabs value={servicePeriod} onValueChange={setServicePeriod}>
            <TabsList>
              <TabsTrigger value="daily" data-testid="pnl-period-daily">Günlük</TabsTrigger>
              <TabsTrigger value="monthly" data-testid="pnl-period-monthly">Aylık</TabsTrigger>
              <TabsTrigger value="yearly" data-testid="pnl-period-yearly">Yıllık</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <KPI icon={TrendingUp} label="Servis Cirosu" value={fmtTRY(sv.revenue)} tone="emerald" />
          <KPI icon={TrendingDown} label="Servis Giderleri" value={fmtTRY(sv.cost)} tone="danger" hint="Personel + genel giderler" />
          <KPI icon={TrendingUp} label="Servis Kârı" value={fmtTRY(sv.profit)} tone={sv.profit >= 0 ? "pine" : "danger"} />
          <KPI icon={Clock} label="Toplam İşçilik Saati" value={`${sv.total_labor_hours.toFixed(1)} sa`} tone="ochre" hint="Teslim edilmiş iş emirleri" />
        </div>

        <Card className="border-slate-200 mt-4">
          <CardContent className="p-4 sm:p-5">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
              {servicePeriod === "daily" ? "Son 31 Gün" : servicePeriod === "monthly" ? "Son 24 Ay" : "Son 10 Yıl"}
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series}>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, name) => name === "labor_hours" ? `${v} sa` : fmtTRY(v)} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" fill="#1B4D3E" radius={[6, 6, 0, 0]} name="Ciro" />
                  <Bar yAxisId="left" dataKey="cost" fill="#EA580C" radius={[6, 6, 0, 0]} name="Gider" />
                  <Line yAxisId="right" dataKey="labor_hours" stroke="#D97706" strokeWidth={2.5} name="Saat" type="monotone" dot={{ r: 3 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 mt-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dönem</TableHead>
                  <TableHead className="text-right">Ciro</TableHead>
                  <TableHead className="text-right">Gider</TableHead>
                  <TableHead className="text-right">Kâr</TableHead>
                  <TableHead className="text-right">Saat</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {series.slice().reverse().map((row) => (
                  <TableRow key={row.period} data-testid={`pnl-svc-${row.period}`}>
                    <TableCell className="font-mono text-sm">{row.period}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-700">{fmtTRY(row.revenue)}</TableCell>
                    <TableCell className="text-right text-rose-600">-{fmtTRY(row.cost)}</TableCell>
                    <TableCell className={`text-right font-extrabold ${row.profit >= 0 ? "text-[#1B4D3E]" : "text-rose-600"}`}>{fmtTRY(row.profit)}</TableCell>
                    <TableCell className="text-right"><Badge variant="outline">{Number(row.labor_hours || 0).toFixed(1)} sa</Badge></TableCell>
                  </TableRow>
                ))}
                {series.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Kayıt yok. İş emri teslim edin ve/veya servis gider kaydı ekleyin.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </Card>

        <div className="mt-2 text-xs text-slate-500">
          İpucu: Servis giderlerini (personel maaşı, elektrik, sarf malzeme vb.) <span className="font-semibold">Finans → Yeni Hareket</span> ekranından
          <span className="font-semibold"> "Gider"</span> ve <span className="font-semibold">Masraf Merkezi: Servis</span> seçerek girin. Sistem, teslim edilmiş iş emirlerinin gelirini otomatik "Servis" merkezine yazar.
        </div>
      </div>

      <CommissionReport />
    </div>
  );
}

function CommissionReport() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    api.get(`/reports/commissions?month=${month}`).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: { total_amount: 0, total_commission: 0 } }));
  }, [month]);
  // last 12 months list
  const months = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return (
    <div className="space-y-3" data-testid="commission-report">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#D97706]">Komisyon</div>
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900">Satış Temsilcisi Komisyonları</h2>
        </div>
        <select
          className="h-9 border border-slate-200 rounded-md px-3 text-sm bg-white"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          data-testid="commission-month"
        >
          {months.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      {data?.rows?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card className="border-slate-200"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Toplam Satış</div>
            <div className="text-2xl font-extrabold text-[#1B4D3E] mt-1">{fmtTRY(data.totals.total_amount)}</div>
          </CardContent></Card>
          <Card className="border-[#D97706]/40 bg-amber-50/60"><CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#D97706]">Toplam Ödenecek Komisyon</div>
            <div className="text-2xl font-extrabold text-[#D97706] mt-1">{fmtTRY(data.totals.total_commission)}</div>
          </CardContent></Card>
        </div>
      )}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Satış Temsilcisi</TableHead>
                <TableHead className="text-right">Komisyon %</TableHead>
                <TableHead className="text-right">Traktör</TableHead>
                <TableHead className="text-right">Parça</TableHead>
                <TableHead className="text-right">Servis</TableHead>
                <TableHead className="text-right">Toplam Satış</TableHead>
                <TableHead className="text-right">Komisyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows || []).map((r) => (
                <TableRow key={r.salesperson_id || "none"} data-testid={`commission-row-${r.salesperson_id || "none"}`}>
                  <TableCell className="font-semibold">{r.name}</TableCell>
                  <TableCell className="text-right text-xs tabular-nums">%{r.commission_rate}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtTRY(r.tractor_sales.amount)}<div className="text-[10px] text-slate-400">{r.tractor_sales.count} adet</div></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtTRY(r.part_orders.amount)}<div className="text-[10px] text-slate-400">{r.part_orders.count} adet</div></TableCell>
                  <TableCell className="text-right tabular-nums">{fmtTRY(r.work_orders.amount)}<div className="text-[10px] text-slate-400">{r.work_orders.count} adet</div></TableCell>
                  <TableCell className="text-right font-extrabold tabular-nums">{fmtTRY(r.total_amount)}</TableCell>
                  <TableCell className="text-right font-extrabold text-[#D97706] tabular-nums">{fmtTRY(r.commission_amount)}</TableCell>
                </TableRow>
              ))}
              {(!data?.rows || data.rows.length === 0) && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">{data === null ? "Yükleniyor…" : "Bu ay satış yok veya temsilci atanmamış."}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
      <div className="text-xs text-slate-500">
        Komisyon % her kullanıcı için <b>Kullanıcılar</b> ekranından ayrı ayrı tanımlanır. Satış/sipariş kayıtlarında "Satış Temsilcisi" alanı doldurulan tüm işlemler burada temsilcisine atanır.
      </div>
    </div>
  );
}
